const _viewerInitScriptPromises = new Map();

function loadViewerInitScript(src) {
  if (_viewerInitScriptPromises.has(src)) return _viewerInitScriptPromises.get(src);

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("The preview component could not be loaded.")),
      { once: true },
    );
    document.head.appendChild(script);
  }).catch((error) => {
    _viewerInitScriptPromises.delete(src);
    throw error;
  });
  _viewerInitScriptPromises.set(src, promise);
  return promise;
}

async function initLazyViewerContent(modal) {
  // CAD previews are initialised by drawing.js after the modal is added. Keep
  // that established path separate; this lazy loader is only for IFC's runtime.
  for (const element of modal.querySelectorAll(
    '[data-needs-init="renderIfc"][data-init-script]',
  )) {
    const functionName = element.dataset.needsInit;
    try {
      if (typeof window[functionName] !== "function") {
        await loadViewerInitScript(element.dataset.initScript);
      }
      const initFunction = window[functionName];
      if (typeof initFunction !== "function") {
        throw new Error("The preview component is unavailable.");
      }
      await Promise.resolve(initFunction(element));
      delete element.dataset.needsInit;
    } catch (error) {
      const message = error instanceof Error ? error.message : "The preview could not be opened.";
      const loading = element.querySelector("[data-ifc-loading]");
      if (loading) loading.textContent = message;
      else element.textContent = message;
      const status = modal.querySelector("[data-ifc-status]");
      if (status) status.textContent = "Preview unavailable";
    }
  }
}

/**
 * File Viewer Modal — image zoom controls & PDF resize fix.
 *
 * Shared between drawing.js (drawing page) and drawing_viewer_common.js
 * (other pages that use the reusable viewer).  Call initFileViewerModal()
 * immediately after inserting the view_file.html modal into the DOM.
 */

/**
 * Initialise zoom controls (images) and PDF resize fix after the
 * file-viewer modal is injected into the DOM.
 */
export async function initFileViewerModal() {
  const modal = document.getElementById("file-viewer-modal");
  if (!modal) return;

  // ── Image zoom ──────────────────────────────────────────────
  const img = modal.querySelector("#viewer-image");
  const wrapper = modal.querySelector("#viewer-image-wrapper");

  if (img && wrapper) {
    img.addEventListener("error", () => {
      wrapper.innerHTML = '<p class="text-red-500">Failed to load image</p>';
    });

    let currentScale = null; // null = fit mode
    const MIN_SCALE = 0.1;
    const MAX_SCALE = 5;
    const STEP = 0.25;

    function getFitScale() {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return 1;
      const cw = wrapper.clientWidth;
      const ch = window.innerHeight * 0.7;
      return Math.min(cw / nw, ch / nh, 1);
    }

    function applyFit() {
      currentScale = null;
      img.style.width = "";
      img.style.height = "";
      img.className = "max-w-full max-h-[70vh] object-contain shadow-lg";
      wrapper.className = "flex items-center justify-center min-h-[460px]";
      updateLabel();
    }

    function applyZoom(scale) {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return; // image not loaded yet
      currentScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
      img.className = "shadow-lg";
      img.style.width = Math.round(nw * currentScale) + "px";
      img.style.height = Math.round(nh * currentScale) + "px";
      wrapper.className = "min-h-[460px]";
      updateLabel();
    }

    function updateLabel() {
      const el = modal.querySelector("#viewer-zoom-level");
      if (!el) return;
      if (currentScale === null) {
        el.textContent = "Fit";
      } else {
        el.textContent = Math.round(currentScale * 100) + "%";
      }
    }

    function getEffectiveScale() {
      return currentScale !== null ? currentScale : getFitScale();
    }

    modal
      .querySelector("#viewer-zoom-in")
      ?.addEventListener("click", () => {
        applyZoom(getEffectiveScale() + STEP);
      });

    modal
      .querySelector("#viewer-zoom-out")
      ?.addEventListener("click", () => {
        applyZoom(getEffectiveScale() - STEP);
      });

    modal
      .querySelector("#viewer-zoom-fit")
      ?.addEventListener("click", applyFit);

    modal
      .querySelector("#viewer-zoom-actual")
      ?.addEventListener("click", () => {
        applyZoom(1);
      });

    // Ctrl/Cmd + scroll-wheel zoom
    const scrollTarget = modal.querySelector(".overflow-auto");
    if (scrollTarget) {
      scrollTarget.addEventListener(
        "wheel",
        (e) => {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY < 0 ? STEP : -STEP;
            applyZoom(getEffectiveScale() + delta);
          }
        },
        { passive: false },
      );
    }
  }

  // ── PDF resize fix ──────────────────────────────────────────
  // Chrome's embedded PDF viewer switches to a compact/mobile mode
  // when browser zoom changes the effective viewport size, and does
  // not revert when zoom is reset.  Detect zoom changes via
  // devicePixelRatio and recreate the <iframe> to force re-render.
  const pdfContainer = modal.querySelector("#viewer-pdf-container");
  if (pdfContainer) {
    let lastDpr = window.devicePixelRatio;
    let debounceTimer;

    function onResize() {
      const dpr = window.devicePixelRatio;
      if (dpr === lastDpr) return;
      lastDpr = dpr;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (!document.getElementById("file-viewer-modal")) return;
        const src = pdfContainer.dataset.pdfSrc;
        const old = pdfContainer.querySelector("iframe");
        if (!old || !src) return;
        const fresh = document.createElement("iframe");
        fresh.id = "viewer-pdf-embed";
        fresh.src = src;
        fresh.className = "w-full h-full border-0";
        fresh.title = modal.querySelector("h3")?.textContent || "";
        pdfContainer.replaceChild(fresh, old);
      }, 300);
    }

    window.addEventListener("resize", onResize);

    // Clean up listener when the modal is removed from DOM
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.removedNodes) {
          if (node === modal) {
            window.removeEventListener("resize", onResize);
            obs.disconnect();
            return;
          }
        }
      }
    });
    if (modal.parentNode) {
      obs.observe(modal.parentNode, { childList: true });
    }
  }

  await initLazyViewerContent(modal);
}

let _controlsRegistered = false;

/**
 * Register the generic close / maximize / download / ESC / backdrop controls
 * for the file-viewer modal.  drawing.js wires these on the drawing page; pages
 * that use the reusable viewer (via drawing_viewer_common.js) call this instead.
 * Uses document-level delegation so it survives modals being inserted/removed,
 * and self-guards so repeated calls register the handlers only once.
 */
export function initFileViewerControls() {
  if (_controlsRegistered) return;
  _controlsRegistered = true;

  function closeModal() {
    const modal = document.getElementById("file-viewer-modal");
    if (!modal) return;
    if (modal.querySelector("[data-cad-url]") && typeof window.destroyCadViewer === "function") {
      Promise.resolve(window.destroyCadViewer())
        .catch((err) => console.error("destroyCadViewer failed:", err))
        .finally(() => modal.remove());
    } else {
      modal.remove();
    }
  }

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && document.getElementById("file-viewer-modal")) closeModal();
  });

  document.addEventListener("click", function (e) {
    const modal = document.getElementById("file-viewer-modal");
    if (!modal) return;

    // Backdrop click
    if (e.target === modal) {
      closeModal();
      return;
    }

    // Close buttons (header X or footer Close)
    if (e.target.closest("#file-viewer-close-btn") || e.target.closest("#closeButton")) {
      closeModal();
      return;
    }

    // Maximize / restore toggle
    if (e.target.closest("#file-viewer-maximize-btn")) {
      const container = document.getElementById("file-viewer-container");
      if (!container) return;
      const btn = e.target.closest("#file-viewer-maximize-btn");
      const pdfContainer = document.getElementById("viewer-pdf-container");
      if (container.dataset.maximized === "true") {
        container.classList.remove("max-w-none", "max-h-none", "h-full", "w-full", "rounded-none");
        container.classList.add("max-w-6xl", "max-h-[90vh]", "rounded-lg");
        container.dataset.maximized = "false";
        btn.title = "Maximize";
        btn.setAttribute("aria-label", "Maximize viewer");
        btn.querySelector("svg").innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"></path>';
        if (pdfContainer) {
          pdfContainer.classList.remove("h-full");
          pdfContainer.classList.add("h-[600px]");
        }
      } else {
        container.classList.remove("max-w-6xl", "max-h-[90vh]", "rounded-lg");
        container.classList.add("max-w-none", "max-h-none", "h-full", "w-full", "rounded-none");
        container.dataset.maximized = "true";
        btn.title = "Restore";
        btn.setAttribute("aria-label", "Restore viewer");
        btn.querySelector("svg").innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"></path>';
        if (pdfContainer) {
          pdfContainer.classList.remove("h-[600px]");
          pdfContainer.classList.add("h-full");
        }
      }
      return;
    }

    // Download button
    if (e.target.closest("#downloadButton")) {
      const fileUrl = modal.dataset.fileUrl;
      const filename = modal.dataset.filename;
      if (!fileUrl || !filename) return;

      const dlBtn = e.target.closest("#downloadButton");
      const oldHtml = dlBtn.innerHTML;
      dlBtn.disabled = true;
      dlBtn.innerHTML = "⏳";

      fetch(fileUrl)
        .then(function (res) {
          if (res.status !== 200) throw new Error("Download failed");
          return res.blob();
        })
        .then(function (blob) {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.classList.add("hidden");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .catch(function () {
          window.open(fileUrl, "_blank");
        })
        .finally(function () {
          dlBtn.disabled = false;
          dlBtn.innerHTML = oldHtml;
        });
    }
  });
}
